import { memo, type ComponentProps } from 'react';
import { DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, Drawer as HerouiDrawer } from '@heroui/react';
import { twMerge } from 'tailwind-merge';

type Props = {
    children: React.ReactNode;
    isOpen: ComponentProps<typeof HerouiDrawer>['isOpen'];
    isDismissable?: ComponentProps<typeof HerouiDrawer>['isDismissable'];
    isKeyboardDismissDisabled?: ComponentProps<typeof HerouiDrawer>['isKeyboardDismissDisabled'];
    header?: React.ReactNode;
    footer?: React.ReactNode;
    bgColor?: 'white' | 'slate-grey-100-alt';
    padding?: 0 | 5;
    onClose?: () => void;
    onOpenChange?: (isOpen: boolean) => void;
    maxHeight?: 'default' | '80vh';
};

function getPaddingClass(padding: Props['padding']) {
    if (padding === 0) return 'p-0';
    if (padding === 5) return 'p-5';
    return 'p-5';
}

function getBgColorClass(bgColor: Props['bgColor']) {
    if (bgColor === 'white') return 'bg-white';
    if (bgColor === 'slate-grey-100-alt') return 'bg-slate-grey-100-alt';
    return 'bg-white';
}

function getMaxHeightClass(maxHeight: Props['maxHeight']) {
    if (maxHeight === '80vh') return 'max-h-[80vh]';
    return '';
}

function getOverflowClass(maxHeight: Props['maxHeight']) {
    if (maxHeight === '80vh') return 'overflow-y-auto';
    return '';
}

function DrawerComponent({
    children,
    isOpen,
    isDismissable = true,
    isKeyboardDismissDisabled = false,
    header,
    footer,
    bgColor = 'white',
    padding = 5,
    onClose,
    onOpenChange,
    maxHeight = 'default',
}: Props) {
    return (
        <HerouiDrawer
            placement="bottom"
            isOpen={isOpen}
            isDismissable={isDismissable}
            isKeyboardDismissDisabled={isKeyboardDismissDisabled}
            onClose={onClose}
            onOpenChange={onOpenChange}
            classNames={{
                base: twMerge(getBgColorClass(bgColor), getMaxHeightClass(maxHeight), 'p-0 rounded-t-8'),
                header: twMerge(getPaddingClass(padding)),
                body: twMerge(
                    getPaddingClass(padding),
                    getOverflowClass(maxHeight),
                    'gap-0 block notch:pb-[calc(env(safe-area-inset-bottom)+30px)]',
                ),
                wrapper: 'border-none z-[100]',
                footer: twMerge(getPaddingClass(padding)),
                closeButton: 'z-20 bg-[radial-gradient(rgba(255,255,255,1)_0%,rgba(255,255,255,0)_100%)]',
            }}
        >
            <DrawerContent>
                {header && <DrawerHeader className={twMerge()}>{header}</DrawerHeader>}
                <DrawerBody>{children}</DrawerBody>
                {footer && <DrawerFooter>{footer}</DrawerFooter>}
            </DrawerContent>
        </HerouiDrawer>
    );
}

export const Drawer = memo(DrawerComponent) as typeof DrawerComponent;
